/**
 * The pick's flight (2026-10-28): after a vote in the article body, the "Your pick" chip flies
 * to the rail card's pick row, so the reader sees where the conversation about it lives.
 * Browser-only, no React: FLIP on a clone, the real chips never move.
 */

/** On the "Your pick" chip in the in-body poll; value = poll id. */
export const PICK_SOURCE_ATTR = 'data-poll-pick-source'

/** On the "Your pick" chip of the rail card's pick row; value = poll id. */
export const PICK_TARGET_ATTR = 'data-poll-pick-target'

export const PICK_FLIGHT_MS = 620

const PICK_FLIGHT_EASE = 'cubic-bezier(0.3, 0.8, 0.3, 1)'

function inViewport(rect: DOMRect): boolean {
  const width = window.innerWidth || document.documentElement.clientWidth
  const height = window.innerHeight || document.documentElement.clientHeight
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= height &&
    rect.right <= width
  )
}

function byAttr(attr: string, pollId: string): HTMLElement | null {
  const value = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(pollId) : pollId
  return document.querySelector<HTMLElement>(`[${attr}="${value}"]`)
}

/** FLIP: clones the source chip, fixes it at the source rect, transitions transform to the target rect, removes it after.
 *  Returns false (and does nothing) under prefers-reduced-motion, when either element is missing, or when either rect is
 *  outside the viewport. Uses cubic-bezier(0.3, 0.8, 0.3, 1). Never throws on the server (guards `document`). */
export function flyPick(pollId: string): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return false
  }
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return false
    }
    const source = byAttr(PICK_SOURCE_ATTR, pollId)
    const target = byAttr(PICK_TARGET_ATTR, pollId)
    if (!source || !target) {
      return false
    }
    const from = source.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    if (!inViewport(from) || !inViewport(to)) {
      return false
    }
    const clone = source.cloneNode(true) as HTMLElement
    // The clone is decoration: no duplicate ids or hooks, nothing a screen reader announces.
    clone.removeAttribute('id')
    clone.removeAttribute(PICK_SOURCE_ATTR)
    clone.setAttribute('aria-hidden', 'true')
    Object.assign(clone.style, {
      position: 'fixed',
      zIndex: '50',
      pointerEvents: 'none',
      margin: '0',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      boxSizing: 'border-box',
      transform: 'translate(0px, 0px)',
      transition: 'none',
    })
    document.body.appendChild(clone)
    // Force a layout read so the start position is committed before the transition starts.
    void clone.offsetWidth
    const dx = to.left - from.left
    const dy = to.top - from.top
    clone.style.transition = `transform ${PICK_FLIGHT_MS}ms ${PICK_FLIGHT_EASE}`
    clone.style.transform = `translate(${dx}px, ${dy}px)`
    let done = false
    const remove = (event?: Event): void => {
      // A transition on a child of the clone bubbles up: only the clone's own ends the flight.
      if (done || (event && event.target !== clone)) {
        return
      }
      done = true
      clone.removeEventListener('transitionend', remove)
      clone.remove()
    }
    // The fallback when no transitionend comes (a hidden tab, a cancelled transition); after a
    // normal landing `done` makes it a no-op.
    setTimeout(remove, PICK_FLIGHT_MS + 100)
    clone.addEventListener('transitionend', remove)
    return true
  } catch {
    return false
  }
}
